import { Badge, Paper } from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { BACKEND_ROUTES } from "../../../../backendRoutes";
import CommentIcon from "@mui/icons-material/Comment";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { FetchErrorAlert } from "../../../../components/FetchErrorAlert";
import { MilestoneForm } from "./MilestoneForm";
import { MilestoneTimelineSkeleton } from "./MilestoneTimelineSkeleton";
import { PAGINATION } from "../../../../constants";
import PropTypes from "prop-types";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineOppositeContent from "@mui/lab/TimelineOppositeContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import Typography from "@mui/material/Typography";
import { formatDateISO } from "../../../../utils/formatTime";
import { hasEditorContent } from "../../../../components";
import { parseISO } from "date-fns";
import { useIsBasicRole } from "../../../../hooks";
import useSWR from "swr";
import { useState } from "react";

export const MilestoneTimeline = ({ contractUuid, alternate = false }) => {
    const isBasicRole = useIsBasicRole();
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedMilestone, setSelectedMilestone] = useState(null);
    // Bumped on every opening to remount the form. Both react-hook-form
    // and the Lexical composer only ever read their initial values, so a
    // form kept mounted across openings would keep serving the milestone
    // as it was the first time it was opened.
    const [formInstance, setFormInstance] = useState(0);

    const {
        data: milestones,
        error: milestonesError,
        mutate,
        isLoading: areMilestonesLoading,
    } = useSWR(
        `${BACKEND_ROUTES.MILESTONE}?parentInfo=true&contractUuid=${contractUuid}&sort=date&limit=${PAGINATION.GENERIC.LIMIT.MAX}`
    );
    const { data: steps, error: stepsError } = useSWR(BACKEND_ROUTES.STEP);

    const handleNewMilestone = () => {
        setSelectedMilestone(null);
        setFormInstance((instance) => instance + 1);
        setOpenDialog(true);
    };
    const handleCloseDialog = () => {
        setOpenDialog(false);
    };
    const handleEditMilestone = (milestone) => {
        setSelectedMilestone(milestone);
        setFormInstance((instance) => instance + 1);
        setOpenDialog(true);
    };

    const mergedFetchError = stepsError ?? milestonesError;
    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;
    if (areMilestonesLoading) return <MilestoneTimelineSkeleton />;

    const existingMilestones = milestones?.rows;

    const availableSteps = steps?.rows.filter(
        (step) =>
            !step.unique ||
            !existingMilestones.some(
                (existingMilestone) =>
                    existingMilestone.Step.label === step.label
            )
    );

    return (
        <>
            <Paper sx={{ p: 0.5 }}>
                {/* "left" is where TimelineContent goes, so the label
                    sits on the left and the date opposite it. */}
                <Timeline
                    position={alternate ? "alternate" : "left"}
                    sx={{ px: 0, minWidth: 240 }}
                >
                    {existingMilestones?.map((milestone) => (
                        <TimelineItemTemplate
                            key={milestone.uuid}
                            oppositeContent={formatDateISO(
                                parseISO(milestone.date)
                            )}
                            milestoneTitle={milestone.Step.label}
                            openDialogForm={() =>
                                handleEditMilestone(milestone)
                            }
                            icon={
                                hasEditorContent(milestone.report) ? (
                                    <Badge
                                        color="primary"
                                        badgeContent={
                                            <CommentIcon
                                                fontSize="12px"
                                                color="white"
                                            />
                                        }
                                        anchorOrigin={{
                                            vertical: "top",
                                            horizontal: "left",
                                        }}
                                        sx={{
                                            "& .MuiBadge-badge": { px: 0.5 },
                                        }}
                                    >
                                        <FactCheckIcon />
                                    </Badge>
                                ) : (
                                    <FactCheckIcon />
                                )
                            }
                        />
                    ))}
                    {!isBasicRole && availableSteps?.length > 0 && (
                        <TimelineItemTemplate
                            oppositeContent={""}
                            milestoneTitle={"Add step"}
                            openDialogForm={handleNewMilestone}
                            icon={<AddCircleOutlineIcon />}
                            finalItem={true}
                        />
                    )}
                </Timeline>
            </Paper>
            <MilestoneForm
                key={formInstance}
                open={openDialog}
                onClose={handleCloseDialog}
                mutateTimeline={mutate}
                contractUuid={contractUuid}
                availableSteps={availableSteps}
                milestone={selectedMilestone}
            />
        </>
    );
};

MilestoneTimeline.propTypes = {
    contractUuid: PropTypes.string.isRequired,
    // Opts back into the original layout, where milestones swap sides
    // as the timeline goes down.
    alternate: PropTypes.bool,
};

const TimelineItemTemplate = ({
    icon,
    oppositeContent,
    milestoneTitle,
    milestoneDescription,
    openDialogForm,
    finalItem = false,
}) => {
    const isBasicRole = useIsBasicRole();
    return (
        <TimelineItem>
            <TimelineOppositeContent
                sx={{
                    m: "auto 0",
                    minWidth: 80,
                    px: 0.5,
                    color: "text.secondary",
                }}
                variant="body2"
            >
                {oppositeContent}
            </TimelineOppositeContent>
            <TimelineSeparator sx={{ px: 0, mx: "2px" }}>
                <TimelineConnector sx={{ minHeight: 20 }} />
                <TimelineDot
                    onClick={isBasicRole ? () => {} : openDialogForm}
                    color="secondary"
                    sx={{ cursor: isBasicRole ? "default" : "pointer" }}
                >
                    {icon}
                </TimelineDot>
                <TimelineConnector
                    sx={{
                        minHeight: 20,
                        visibility: finalItem ? "hidden" : "visible",
                    }}
                />
            </TimelineSeparator>
            <TimelineContent sx={{ py: "12px", px: 0.5, my: "auto" }}>
                <Typography variant="subtitle2" component="span">
                    {milestoneTitle}
                </Typography>
                <Typography>{milestoneDescription}</Typography>
            </TimelineContent>
        </TimelineItem>
    );
};
